import { Injectable } from '@angular/core';
import { skyPoseImageUrl } from './sky-assets';

export type SkyPose =
  | 'ready'
  | 'thinking'
  | 'focused'
  | 'concerned'
  | 'encouraging'
  | 'excited'
  | 'proud'
  | 'confused'
  | 'welcome';

export interface SkyCoachContext {
  surface?: 'launch' | 'dashboard' | 'results' | 'settings';
  isLoading?: boolean;
  hasExpiredTrial?: boolean;
  canStartNewTest?: boolean;
  recommendationType?: string;
  readinessStatus?: string;
  remainingToday?: number;
  onTrack?: boolean;
  streakDay?: number;
  reason?: string;
  subjectName?: string;
  dueReviews?: number;
  reminderTitle?: string;
  reminderBody?: string;
  testPassed?: boolean | null;
  isExamMode?: boolean;
  weakTopicsCount?: number;
  dailyGoalMet?: boolean;
  allCorrect?: boolean;
}

export interface SkyCoachState {
  pose: SkyPose;
  message: string;
  statusLabel?: string;
}

@Injectable({ providedIn: 'root' })
export class SkyCoachStateService {
  private readonly playedSituationKeys = new Set<string>();
  private launchTransitionActive = false;

  beginLaunchTransition(): void {
    this.launchTransitionActive = true;
  }

  endLaunchTransition(): void {
    this.launchTransitionActive = false;
  }

  shouldPlaySituation(context: SkyCoachContext, state: SkyCoachState): boolean {
    if (context.isLoading) {
      return false;
    }

    const surface = context.surface ?? 'dashboard';
    const key = `${surface}:${state.pose}`;
    if (surface === 'dashboard' && this.launchTransitionActive) {
      this.playedSituationKeys.add(key);
      return false;
    }
    if (this.playedSituationKeys.has(key)) {
      return false;
    }

    this.playedSituationKeys.add(key);
    return true;
  }

  resolve(context: SkyCoachContext): SkyCoachState {
    if (context.surface === 'launch') {
      return {
        pose: 'welcome',
        message: 'Welcome back. Let\'s keep your training moving.',
      };
    }

    if (context.surface === 'settings') {
      return {
        pose: 'thinking',
        message: 'Set a finish date and I\'ll turn the full course into a daily workload you can actually keep.',
        statusLabel: 'Planning',
      };
    }

    if (context.surface === 'results') {
      return this.resolveResults(context);
    }

    return this.resolveDashboard(context);
  }

  poseAssetUrl(pose: SkyPose): string {
    return skyPoseImageUrl(pose);
  }

  formatReadinessStatus(status?: string): string {
    if (!status) {
      return 'Not started';
    }
    return status
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private resolveDashboard(context: SkyCoachContext): SkyCoachState {
    const statusLabel = this.formatReadinessStatus(context.readinessStatus);

    if (context.isLoading) {
      return {
        pose: 'thinking',
        message: 'I\'m checking your training plan for today.',
        statusLabel: 'Planning',
      };
    }

    if (context.hasExpiredTrial && context.canStartNewTest === false) {
      return {
        pose: 'concerned',
        message: 'Your trial has ended. Subscribe to keep training and stay on your ATPL plan.',
        statusLabel,
      };
    }

    if (context.reminderBody || context.reminderTitle) {
      return {
        pose: context.reminderTitle === 'Keep your streak' ? 'encouraging' : 'ready',
        message: this.cleanReminder(context.reminderBody || context.reminderTitle || ''),
        statusLabel: context.reminderTitle || 'Reminder',
      };
    }

    if (!context.recommendationType) {
      return {
        pose: 'welcome',
        message: 'I\'ll show target-based motivation here once a course plan exists.',
      };
    }

    switch (context.recommendationType) {
      case 'SET_COURSE_GOAL':
        return {
          pose: 'thinking',
          message: 'Pick a pace in Training target. Then I\'ll keep today\'s workload visible.',
          statusLabel: 'Planning',
        };
      case 'NO_SUBJECTS':
        return {
          pose: 'confused',
          message: 'I couldn\'t find an active subject for this course. Check your course setup.',
          statusLabel,
        };
    }

    if (
      context.remainingToday === 0
      && context.recommendationType !== 'SET_COURSE_GOAL'
      && context.recommendationType !== 'NO_SUBJECTS'
    ) {
      return {
        pose: 'proud',
        message: 'You\'re on track today. Keep the streak warm when new reviews become due.',
        statusLabel,
      };
    }

    switch (context.recommendationType) {
      case 'DUE_WRONG_REVIEW':
        return {
          pose: 'concerned',
          message: this.withSubject(
            context,
            `${context.dueReviews ?? 'Several'} wrong answers are due. Clear them before new questions pile up.`
          ),
          statusLabel: statusLabel || 'Reviewing wrong',
        };
      case 'UNSEEN_STUDY':
        return {
          pose: 'ready',
          message: this.withSubject(context, 'New questions are waiting. A short session keeps momentum going.'),
          statusLabel,
        };
      case 'EXAM_CANDIDATE':
        return {
          pose: 'excited',
          message: this.withSubject(
            context,
            'You\'ve seen nearly everything here and errors are low. Exam mode is worth a try.'
          ),
          statusLabel: statusLabel || 'Exam candidate',
        };
      case 'EXAM_READY':
        return {
          pose: 'proud',
          message: this.withSubject(context, 'This subject looks stable. Keep it warm with periodic review.'),
          statusLabel: statusLabel || 'Exam ready',
        };
      case 'NEXT_SUBJECT':
        return {
          pose: 'proud',
          message: this.withSubject(context, 'Nice work here. You\'re ready to move on to the next subject.'),
          statusLabel: statusLabel || 'Ready for next',
        };
      case 'CONTINUE_SUBJECT':
        if (context.readinessStatus === 'reviewing_wrong') {
          return {
            pose: 'concerned',
            message: this.withSubject(context, 'A few topics keep slipping. Review wrong answers before pushing ahead.'),
            statusLabel,
          };
        }
        return {
          pose: 'encouraging',
          message: this.withSubject(context, 'Keep building coverage here. Steady sessions beat cramming.'),
          statusLabel,
        };
      default:
        break;
    }

    if (context.remainingToday != null && context.remainingToday > 0) {
      const noun = context.remainingToday === 1 ? 'question' : 'questions';
      return {
        pose: context.streakDay ? 'encouraging' : 'ready',
        message: `${context.remainingToday} ${noun} left today keeps your plan on track.`,
        statusLabel,
      };
    }

    return {
      pose: 'encouraging',
      message: context.reason || 'Ready when you are. Open Train next to see your next session.',
      statusLabel,
    };
  }

  private resolveResults(context: SkyCoachContext): SkyCoachState {
    const statusLabel = this.formatReadinessStatus(context.readinessStatus);
    const nextType = context.recommendationType;

    if (context.isExamMode) {
      if (context.testPassed) {
        return {
          pose: 'excited',
          message: 'Exam passed. That\'s a real milestone — lock it in with a short review soon.',
          statusLabel: statusLabel || 'Exam passed',
        };
      }
      return {
        pose: 'concerned',
        message: 'Tough exam run. Review the wrong answers now while the mistakes are still fresh.',
        statusLabel: statusLabel || 'Exam failed',
      };
    }

    if (context.allCorrect) {
      return {
        pose: 'proud',
        message: 'Clean sheet. Every answer correct — strong session.',
        statusLabel,
      };
    }

    if (context.dailyGoalMet) {
      return {
        pose: 'proud',
        message: 'Daily target met. You can stop here or take one more short focused session.',
        statusLabel,
      };
    }

    if (nextType === 'RECOVERY_MIX' || (context.weakTopicsCount ?? 0) > 0) {
      return {
        pose: 'focused',
        message: context.reason
          || 'We\'ll fix what slipped first, then mix in new questions from the same areas.',
        statusLabel,
      };
    }

    if (nextType === 'DUE_WRONG_REVIEW') {
      return {
        pose: 'encouraging',
        message: context.reason || 'Wrong-answer reviews are due. That\'s the fastest way to recover.',
        statusLabel,
      };
    }

    if (nextType === 'EXAM_CANDIDATE' || nextType === 'EXAM_READY' || nextType === 'NEXT_SUBJECT') {
      return {
        pose: 'excited',
        message: context.reason || 'You\'re close to the next level in this subject.',
        statusLabel,
      };
    }

    if (context.remainingToday != null && context.remainingToday > 0) {
      const noun = context.remainingToday === 1 ? 'question' : 'questions';
      return {
        pose: 'encouraging',
        message: `${context.remainingToday} ${noun} left today to stay on plan.`,
        statusLabel,
      };
    }

    return {
      pose: 'ready',
      message: context.reason || 'Good session. Your next step is ready when you are.',
      statusLabel,
    };
  }

  private cleanReminder(value: string): string {
    return value.replace(/<[^>]+>/g, '').trim();
  }

  private withSubject(context: SkyCoachContext, message: string): string {
    if (!context.subjectName) {
      return message;
    }
    return `${context.subjectName}: ${message}`;
  }
}
